import { TabPanel } from "@mui/lab";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Switch,
  ThemeProvider,
  Tooltip,
  Typography,
} from "@mui/material";
import { Icon } from "@fluentui/react/lib/Icon";
import { DataGrid, GridCellParams } from "@mui/x-data-grid";
import { muiTheme } from "system/styles/muiTheme";
import { useEffect, useState } from "react";
import { AccessEntryInfo } from "system/types/wireTypes";
import { getSorter } from "system/libraries/utilities";
import { useApi } from "system/hooks";
import AccessForm from "./AccessForm";
import { ConfirmationDialog, SuccessMessage } from "components/ui";
import { Roles, RoleLabels, Role } from "system/types/enums/Roles";

export const AccessTab = () => {
  const [membershipLoading, setMembershipLoading] = useState(true);
  // Access state
  const [accessList, setAccessList] = useState<AccessEntryInfo[]>([]);

  // Grant Access Dialog
  const [editDialogVisible, setEditDialogVisible] = useState(false);
  const [accessDialogVisible, setAccessDialogVisible] = useState(false);
  const [confirmationDialogVisible, setConfirmationDialogVisible] = useState(false);
  const [successMessageVisible, setSuccessMessageVisible] = useState(false);
  const [successMessageTitle, setSuccessMessageTitle] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [userSelected, setUserSelected] = useState<AccessEntryInfo | null>(null);
  const [isFormSubmitting, setIsFormSubmitting] = useState(false);

  const api = useApi();

  const loadAccessList = async () => {
    setMembershipLoading(true);
    const response = await api.access.getAcl();
    setAccessList((response ?? []).sort(getSorter("name")));
    setMembershipLoading(false);
  };

  useEffect(() => {
    loadAccessList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Columns for table in Access
  const accessCol = [
    { field: "name", headerName: "Name", flex: 1 },
    { field: "email", headerName: "Email", flex: 1 },
    {
      field: "roles",
      headerName: "Roles",
      flex: 2,
      renderCell: (params: GridCellParams) => {
        const roles = params.value as string[];
        return (
          <Box
            sx={{
              display: "flex",
              gap: 1,
              flexWrap: "wrap",
              padding: 1,
              maxWidth: "100%",
            }}
          >
            {roles.map((role) => (
              <Chip
                key={role}
                label={RoleLabels[role as Role]}
                size="small"
                sx={{
                  backgroundColor: role === Roles.Admin ? "#d32f2f" : "#1976d2",
                  color: "#ffffff",
                  marginBottom: 0.5,
                }}
              />
            ))}
          </Box>
        );
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 0.5,
      renderCell: (params: GridCellParams) => (
        <>
          <Tooltip title={"Edit Roles"} arrow>
            <IconButton
              size="small"
              sx={{ color: "#8a8886" }}
              onClick={() => {
                setUserSelected(params.row);
                setEditDialogVisible(true);
              }}
            >
              <Icon iconName="Edit" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Revoke Access" arrow>
            <IconButton
              size="small"
              color="error"
              onClick={() => {
                setUserSelected(params.row);
                setConfirmationDialogVisible(true);
              }}
            >
              <Icon iconName="Delete" style={{ color: "#d5878b" }} />
            </IconButton>
          </Tooltip>
        </>
      ),
    },
  ];

  const accessRows = [...accessList].sort(getSorter("name")).map((entry) => ({
    id: entry.userId,
    userId: entry.userId,
    groupId: entry.groupId,
    name: entry.name,
    email: entry.email,
    roles: entry.roles,
  }));

  const handleEditDialogClose = () => {
    setEditDialogVisible(false);
    setUserSelected(null);
  };

  const handleEditDialogCloseEvents = (event: {}, reason: "backdropClick" | "escapeKeyDown") => {
    if (reason !== "backdropClick" && reason !== "escapeKeyDown") {
      handleEditDialogClose();
    }
  };

  const handleAccessDialogClose = () => {
    setAccessDialogVisible(false);
  };

  const handleAccessDialogCloseEvents = (event: {}, reason: "backdropClick" | "escapeKeyDown") => {
    if (reason !== "backdropClick" && reason !== "escapeKeyDown") {
      handleAccessDialogClose();
    }
  };

  const handleConfirmRevokeAccess = async () => {
    if (!userSelected) return;

    const result = await api.access.revoke(userSelected);

    if (result.isSuccess) {
      setAccessList((oldList) => oldList.filter((entry) => entry.userId !== userSelected.userId));
      setUserSelected(null);
      setConfirmationDialogVisible(false);
      setSuccessMessageTitle("Access revoked successfully");
      handleAccessDialogClose();
      setSuccessMessageVisible(true);
    } else {
      setErrorMessage(result.failMessage ?? "Failed to revoke access.");
    }
  };

  const handleAccessFormSuccess = (user: AccessEntryInfo) => {
    setSuccessMessageTitle("Access granted successfully");
    handleAccessDialogClose();
    setSuccessMessageVisible(true);
    setAccessList((oldList) => {
      const existingUserIndex = oldList.findIndex((entry) => entry.userId === user.userId);
      if (existingUserIndex !== -1) {
        const updatedList = [...oldList];
        updatedList[existingUserIndex] = user;
        return updatedList;
      }
      return [...oldList, user];
    });
    setIsFormSubmitting(false);
  };

  const handleEditFormSuccess = (user: AccessEntryInfo) => {
    if (!userSelected) return;
    setAccessList((oldList) => {
      const existingUserIndex = oldList.findIndex((entry) => entry.userId === user.userId);
      if (existingUserIndex !== -1) {
        const updatedList = [...oldList];
        updatedList[existingUserIndex] = user;
        return updatedList;
      }
      return oldList;
    });
    setSuccessMessageTitle("User roles updated successfully");
    setSuccessMessageVisible(true);
    handleEditDialogClose();
    setIsFormSubmitting(false);
  };

  return (
    <>
      <TabPanel value="users">
        <div className="admin-buttons">
          <Button className="grantAccess" variant="contained" onClick={() => setAccessDialogVisible(true)}>
            Grant Access
            <div className="permissions-icon">
              <Icon iconName="Permissions" />
            </div>
          </Button>
          <Button className="refresh" variant="outlined" onClick={() => loadAccessList()}>
            Refresh
            <div className="refresh-icon">
              <Icon iconName="refresh" />
            </div>
          </Button>
          <div className="hint-container">
            <Tooltip title="Permission to each email address that should have access." placement="top-end">
              <div className="hint-icon">
                <IconButton aria-label="hint">
                  <Icon iconName="info" style={{ color: "white" }} />
                </IconButton>
              </div>
            </Tooltip>
          </div>
        </div>

        {membershipLoading ? (
          <div>Loading</div>
        ) : (
          <ThemeProvider theme={muiTheme}>
            <div style={{ height: "640px", width: "100%" }}>
              <DataGrid
                rows={accessRows}
                columns={accessCol}
                initialState={{
                  pagination: {
                    paginationModel: {
                      pageSize: 10,
                    },
                  },
                }}
                pageSizeOptions={[10, 20, 50, 100]}
                disableRowSelectionOnClick
                getRowHeight={() => "auto"}
                sx={{
                  "& .MuiDataGrid-cell": {
                    borderBottom: "1px solid #424242",
                    backgroundColor: "#3c3c3c",
                    color: "#ffffff",
                    whiteSpace: "normal",
                    wordWrap: "break-word",
                    lineHeight: "1.5",
                    paddingTop: "5px",
                    overflow: "visible",
                  },
                  "& .MuiDataGrid-columnHeaders": {
                    backgroundColor: "#1e1e1e",
                    borderBottom: "1px solid #424242",
                    color: "white",
                  },
                  "& .MuiDataGrid-footerContainer": {
                    backgroundColor: "#1e1e1e",
                    borderTop: "1px solid #424242",
                    color: "white",
                    justifyContent: "center",
                  },
                  "& .MuiTablePagination-toolbar": {
                    color: "white",
                    alignItems: "baseline",
                    paddingTop: "10px",
                  },
                }}
              />
            </div>
          </ThemeProvider>
        )}
      </TabPanel>
      <Dialog
        open={accessDialogVisible}
        onClose={handleAccessDialogCloseEvents}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            backgroundColor: "#1b1a19",
            zIndex: 1400,
            maxHeight: "80vh",
            overflow: "auto",
          },
        }}
      >
        <DialogTitle sx={{ backgroundColor: "#1b1a19", color: "#ffffff" }}>
          <Typography variant="inherit" fontWeight="bold" color="#ffffff">
            Add User Roles
          </Typography>
        </DialogTitle>
        <DialogContent
          sx={{
            backgroundColor: "#1b1a19",
            display: "flex",
            flexDirection: "column",
            gap: "1em",
          }}
        >
          <AccessForm done={handleAccessFormSuccess} setIsFormSubmitting={setIsFormSubmitting} />
        </DialogContent>
        <DialogActions sx={{ backgroundColor: "#1b1a19" }}>
          <Button
            onClick={handleAccessDialogClose}
            variant="outlined"
            sx={{ color: "#ffffff", borderColor: "#757575" }}
            disabled={isFormSubmitting}
          >
            Cancel
          </Button>
          <Button
            form="AccessForm"
            type="submit"
            variant="contained"
            sx={{
              backgroundColor: "#1a73e8",
              borderColor: "#1a73e8",
              color: "#ffffff",
              "&:hover": { backgroundColor: "#1669c1", borderColor: "#1669c1" },
            }}
            disabled={isFormSubmitting}
          >
            {isFormSubmitting ? <CircularProgress size={24} /> : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={editDialogVisible}
        onClose={handleEditDialogCloseEvents}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            backgroundColor: "#1b1a19",
            zIndex: 1400,
            maxHeight: "80vh",
            overflow: "auto",
          },
        }}
      >
        <DialogTitle sx={{ backgroundColor: "#1b1a19", color: "#ffffff" }}>
          <Typography variant="inherit" fontWeight="bold" color="#ffffff">
            Edit User Roles
          </Typography>
        </DialogTitle>
        <DialogContent
          sx={{
            backgroundColor: "#1b1a19",
            display: "flex",
            flexDirection: "column",
            gap: "1em",
          }}
        >
          <AccessForm done={handleEditFormSuccess} user={userSelected ?? undefined} setIsFormSubmitting={setIsFormSubmitting} />
        </DialogContent>
        <DialogActions sx={{ backgroundColor: "#1b1a19" }}>
          <Button
            onClick={handleEditDialogClose}
            variant="outlined"
            sx={{ color: "#ffffff", borderColor: "#757575" }}
            disabled={isFormSubmitting}
          >
            Cancel
          </Button>
          <Button
            form="AccessForm"
            type="submit"
            variant="contained"
            sx={{
              backgroundColor: "#1a73e8",
              borderColor: "#1a73e8",
              color: "#ffffff",
              "&:hover": { backgroundColor: "#1669c1", borderColor: "#1669c1" },
            }}
            disabled={isFormSubmitting}
          >
            {isFormSubmitting ? <CircularProgress size={24} /> : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmationDialog
        title="Revoke Access"
        subText={`Are you sure you want to revoke all access from ${userSelected?.name}?`}
        isVisible={confirmationDialogVisible}
        onConfirm={handleConfirmRevokeAccess}
        onCancel={() => {
          setConfirmationDialogVisible(false);
          setUserSelected(null);
        }}
      />
      <SuccessMessage
        isVisible={successMessageVisible}
        onConfirm={() => {
          setSuccessMessageVisible(false);
        }}
        title={successMessageTitle}
        message={"Your changes have been saved."}
      />
      {errorMessage && <Box sx={{ color: "#d32f2f", marginTop: "10px" }}>{errorMessage}</Box>}
    </>
  );
};
